import { z } from 'zod/v4';

import { endpoint } from '../http/endpoint';
import {
    ManufacturingKvKeyValueInputRuntype,
    ManufacturingKvKeyValueRuntype,
    ManufacturingKvKeyValueUpdateRuntype,
} from './manufacturingKvBackendTypes';

/**
 * Endpoints for the manufacturing key-value store.
 *
 * A single prototyping store. The tenant must have manufacturing access, and
 * the user needs manufacturing view permission to read and manufacturing edit
 * permission to write. The value is a JSON string, (de)serialized by the
 * caller, mirroring the flexible-calculation key-value store.
 */
export const manufacturingKvEndpoints = {
    'GET /manufacturing-kv-store': endpoint({
        description: 'List all key-value pairs in the manufacturing store',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.array(ManufacturingKvKeyValueRuntype),
        actionPermission: () => 'CanViewManufacturingKv',
    }),

    'GET /manufacturing-kv-store/:key': endpoint({
        description: 'Retrieve a single key-value pair from the manufacturing store',
        pathParams: z.object({ key: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: ManufacturingKvKeyValueRuntype,
        actionPermission: () => 'CanViewManufacturingKv',
    }),

    'POST /manufacturing-kv-store': endpoint({
        description: 'Create a new key-value pair in the manufacturing store (409 if key exists)',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: ManufacturingKvKeyValueInputRuntype,
        responseBody: ManufacturingKvKeyValueRuntype,
        invalidates: ['GET /manufacturing-kv-store', 'GET /manufacturing-kv-store/:key'],
        actionPermission: () => 'CanEditManufacturingKv',
    }),

    'PATCH /manufacturing-kv-store/:key': endpoint({
        description: 'Update the value for an existing key in the manufacturing store',
        pathParams: z.object({ key: z.string() }),
        queryParams: z.undefined(),
        requestBody: ManufacturingKvKeyValueUpdateRuntype,
        responseBody: ManufacturingKvKeyValueRuntype,
        invalidates: ['GET /manufacturing-kv-store', 'GET /manufacturing-kv-store/:key'],
        actionPermission: () => 'CanEditManufacturingKv',
    }),

    'DELETE /manufacturing-kv-store/:key': endpoint({
        description: 'Delete a key-value pair from the manufacturing store',
        pathParams: z.object({ key: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.union([z.undefined(), z.literal('')]),
        invalidates: ['GET /manufacturing-kv-store', 'GET /manufacturing-kv-store/:key'],
        actionPermission: () => 'CanEditManufacturingKv',
    }),
};
